/**
 * The text DJI Fly keeps beside a mission's KMZ as `<name>.txt`: the mission name first, then
 * the flight parameters one per line, for reading on the controller.
 */
import { shutterDenominator } from '@/planner/stats';
import type { MissionInputs, Plan } from '@/planner/types';

type T = (key: string) => string;

export function missionSummary(
  m: { name: string; droneName: string; inputs: MissionInputs; plan: Plan; missionId: string; exportedAt: string },
  t: T,
  number: (n: number, digits?: number) => string,
): string {
  const { inputs, plan } = m;
  const yesNo = (v: boolean) => t(v ? 'summary.yes' : 'summary.no');
  return [
    m.name,
    `${t('panel.drone')}: ${m.droneName}`,
    `${t('panel.altitude')}: ${number(inputs.altitude_m)}\u00a0m`,
    `${t('summary.overlap')}: ${number(inputs.forward_overlap)}/${number(inputs.side_overlap)}\u00a0%`,
    `${t('panel.speed')}: ${number(plan.speed_ms, 1)}\u00a0m/s`,
    `${t('panel.trigger')}: ${t(inputs.trigger === 'interval' ? 'panel.triggerInterval' : 'panel.triggerWaypoint')}`,
    ...(inputs.trigger === 'interval' ? [`${t('panel.interval')}: ${number(inputs.interval_s, 1)}\u00a0s`] : []),
    ...(inputs.hover_s > 0 ? [`${t('panel.hover')}: ${number(inputs.hover_s, 1)}\u00a0s`] : []),
    `${t('stats.photos')}: ${number(plan.stats.photos)}`,
    `${t('stats.lines')}: ${number(plan.stats.lines)}`,
    `${t('stats.shutter')}: 1/${number(shutterDenominator(plan.speed_ms, plan.stats.gsd_cm))}\u00a0s`,
    `${t('stats.spacing')}: ${number(plan.stats.line_spacing_m, 1)} / ${number(plan.stats.photo_spacing_m, 1)}\u00a0m`,
    `${t('stats.gsd')}: ${number(plan.stats.gsd_cm, 2)}\u00a0cm/px`,
    `${t('stats.area')}: ${number(plan.stats.area_ha, 2)}\u00a0ha`,
    `${t('panel.margin')}: ${number(inputs.margin_m)}\u00a0m`,
    `${t('panel.angle')}: ${number(plan.angle_deg)}°`,
    `${t('panel.gimbalPitch')}: ${number(inputs.gimbal_pitch_deg)}°`,
    `${t('panel.rcLost')}: ${t(`panel.rcLostOptions.${inputs.rc_lost}`)}`,
    `${t('panel.finish')}: ${t(`panel.finishOptions.${inputs.finish}`)}`,
    `${t('panel.terrainFollow')}: ${yesNo(inputs.terrain_follow)}`,
    `Forestum ${m.missionId} · ${m.exportedAt}`,
  ].join('\n');
}
